import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { DeepPartial } from 'react-hook-form';
import {
  CasafariListingModel,
  CasafariPropertyModel,
  PropertyModel,
} from 'src/gql/graphql';
import { fDateTime } from 'src/utils/format-time';
import { formatMoney, formatPerSqm } from 'src/utils/format-number';

type Props = {
  property: DeepPartial<PropertyModel>;
};

function formatCoordinatesSource(value?: number | null) {
  if (value === null || value === undefined) return '—';
  const map: Record<number, string> = {
    4: 'Manually set coordinates (highest accuracy)',
    9: 'From a single listing (source-dependent accuracy)',
    11: 'Confirmed with address (high accuracy)',
    2: 'Most common from listings (semi-accurate)',
    10: 'Random within 50m from location center',
    8: 'Random within boundary or 2000m from center',
    3: 'Fallback: location center coordinates',
    12: 'From cadastral data (high accuracy)',
  };
  const label = map[value];
  return label ? `${value} — ${label}` : `${value} — Unknown`;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
      {children}
    </Typography>
  );
}

function InfoItem({
  label,
  children,
}: {
  label: string;
  children?: React.ReactNode;
}) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', textTransform: 'uppercase' }}
      >
        {label}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        {children ?? (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            —
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function statusColor(status?: string | null) {
  const s = (status || '').toLowerCase();
  if (!s) return 'default' as const;
  if (s.includes('active') || s.includes('available'))
    return 'success' as const;
  if (s.includes('sold') || s.includes('off')) return 'error' as const;
  if (s.includes('rented') || s.includes('lease')) return 'info' as const;
  if (s.includes('pending') || s.includes('reserve') || s.includes('under'))
    return 'warning' as const;
  return 'default' as const;
}

// formatMoney and formatPerSqm moved to src/utils/format-number

function Chips({ items }: { items?: (string | null | undefined)[] | null }) {
  if (!items || !items.length)
    return <Typography variant="body2">—</Typography>;
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {items.filter(Boolean).map((x) => (
        <Chip key={String(x)} label={x} size="small" variant="outlined" />
      ))}
    </Stack>
  );
}

function FeaturesRawItems({ featuresRaw }: { featuresRaw?: any }) {
  if (!featuresRaw) return null;
  const { floor, orientation, views, directions, characteristics } =
    (featuresRaw as any) || {};
  return (
    <>
      <Grid item xs={12} sm={6}>
        <InfoItem label="Floor">{floor ? String(floor) : undefined}</InfoItem>
      </Grid>
      <Grid item xs={12} sm={6}>
        <InfoItem label="Orientation">
          {orientation ? String(orientation) : undefined}
        </InfoItem>
      </Grid>
      <Grid item xs={12}>
        <InfoItem label="Views">
          {Array.isArray(views) && views.length ? (
            <Chips items={views} />
          ) : undefined}
        </InfoItem>
      </Grid>
      <Grid item xs={12}>
        <InfoItem label="Directions">
          {Array.isArray(directions) && directions.length ? (
            <Chips items={directions} />
          ) : undefined}
        </InfoItem>
      </Grid>
      <Grid item xs={12}>
        <InfoItem label="Characteristics">
          {Array.isArray(characteristics) && characteristics.length ? (
            <Chips items={characteristics} />
          ) : undefined}
        </InfoItem>
      </Grid>
    </>
  );
}

function SalePriceLastChange({
  change,
  currency,
}: {
  change?: any;
  currency?: string | null;
}) {
  const lc: any = change;
  if (!lc) return null;
  const entry = Array.isArray(lc) ? lc[0] : lc;
  if (!entry) return null;

  const oldV =
    entry.old_value !== undefined && entry.old_value !== null
      ? Number(entry.old_value)
      : undefined;
  const newV =
    entry.new_value !== undefined && entry.new_value !== null
      ? Number(entry.new_value)
      : undefined;
  const date = entry.change_date;

  const hasNumbers = typeof oldV === 'number' && typeof newV === 'number';
  let deltaStr: string | null = null;
  if (hasNumbers && Number.isFinite(oldV!) && Number.isFinite(newV!)) {
    const delta = (newV as number) - (oldV as number);
    const pct = (oldV as number) !== 0 ? (delta / (oldV as number)) * 100 : 0;
    let sign = '';
    if (delta > 0) sign = '+';
    else if (delta < 0) sign = '−';
    deltaStr = `${sign}${Math.abs(delta).toLocaleString()}${
      currency ? ` ${currency}` : ''
    } (${sign}${Math.abs(pct).toFixed(1)}%)`;
  }

  let direction = '';
  if (hasNumbers) {
    const oldNum = oldV as number;
    const newNum = newV as number;
    if (newNum > oldNum) direction = '↑';
    else if (newNum < oldNum) direction = '↓';
    else direction = '→';
  }

  let color: 'error' | 'success' | 'default' = 'default';
  if (hasNumbers) {
    const oldNum = oldV as number;
    const newNum = newV as number;
    if (newNum > oldNum) color = 'error';
    else if (newNum < oldNum) color = 'success';
    else color = 'default';
  }

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" gap={1} alignItems="center">
        {direction ? (
          <Chip size="small" label={direction} color={color as any} />
        ) : null}
        {date ? (
          <Typography variant="body2">{fDateTime(date as any)}</Typography>
        ) : null}
      </Stack>
      {hasNumbers ? (
        <Typography variant="body2">
          {formatMoney(oldV as any, currency as any)} →{' '}
          {formatMoney(newV as any, currency as any)}
        </Typography>
      ) : null}
      {deltaStr ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Δ {deltaStr}
        </Typography>
      ) : null}
    </Stack>
  );
}

function CasafariDetails({ data }: { data: Partial<CasafariPropertyModel> }) {
  const { saleStatus } = data;

  return (
    <Stack gap={3}>
      <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'background.neutral' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6">
                {formatMoney(data.salePriceBase, data.saleCurrency)}
              </Typography>
              <Chip
                size="small"
                label={saleStatus || '—'}
                color={statusColor(saleStatus) as any}
              />
              <Chip
                size="small"
                label="Exclusive"
                color={data.saleExclusive ? 'primary' : 'default'}
                variant={data.saleExclusive ? 'filled' : 'outlined'}
              />
              <Chip
                size="small"
                label="Below market"
                color={data.saleIsBelowMarket ? 'warning' : 'default'}
                variant={data.saleIsBelowMarket ? 'filled' : 'outlined'}
              />
              <Chip
                size="small"
                label="Untracked period"
                color={data.saleHasUntrackedPeriod ? 'warning' : 'default'}
                variant={data.saleHasUntrackedPeriod ? 'filled' : 'outlined'}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sale: {formatPerSqm(data.salePricePerSqm, data.saleCurrency)}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Divider />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SectionTitle>Overview</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Casafari Property ID">
                {data.casafariPropertyId}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Unit ID">{data.casafariUnitId}</InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Primary Listing ID">
                {data.casafariPrimaryListingId}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Location ID">{data.casafariLocationId}</InfoItem>
            </Grid>
            <Grid item xs={12}>
              <InfoItem label="Casafari URL">
                {data.casafariUrl ? (
                  <Link href={data.casafariUrl} target="_blank" rel="noopener">
                    {data.casafariUrl}
                  </Link>
                ) : null}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Type Group">{data.typeGroup}</InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Country ID">{data.countryId}</InfoItem>
            </Grid>
            <Grid item xs={12}>
              <InfoItem label="Operations">
                <Chips items={data.operations} />
              </InfoItem>
            </Grid>
            <Grid item xs={12}>
              <InfoItem label="Business Operations">
                <Chips items={data.businessOperations} />
              </InfoItem>
            </Grid>

            <Grid item xs={12} sm={6}>
              <InfoItem label="Coordinates Source">
                {formatCoordinatesSource(
                  data.coordinatesSource as number | null
                )}
              </InfoItem>
            </Grid>
            <Grid item xs={12}>
              <InfoItem label="Ref Numbers">
                <Chips
                  items={data.refNumbers as (string | null | undefined)[]}
                />
              </InfoItem>
            </Grid>
            <FeaturesRawItems featuresRaw={(data as any).featuresRaw} />
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTitle>Sale Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Status">
                {saleStatus ? (
                  <Chip
                    size="small"
                    label={saleStatus}
                    color={statusColor(saleStatus) as any}
                  />
                ) : (
                  '—'
                )}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Currency">{data.saleCurrency}</InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Price Base">
                {formatMoney(data.salePriceBase, data.saleCurrency)}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="€/sqm">
                {formatPerSqm(data.salePricePerSqm, data.saleCurrency)}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Base Market Value">
                {formatMoney(
                  data.salePriceBaseMarketValue as any,
                  data.saleCurrency
                )}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="€/sqm Base">
                {formatPerSqm(
                  data.salePricePerSqmBase as any,
                  data.saleCurrency
                )}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="€/sqm Base Market Value">
                {formatPerSqm(
                  data.salePricePerSqmBaseMarketValue as any,
                  data.saleCurrency
                )}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Date Start">
                {data.saleDateStart
                  ? fDateTime(data.saleDateStart as any)
                  : '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Date End">
                {data.saleDateEnd ? fDateTime(data.saleDateEnd as any) : '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Price Last Change">
                <SalePriceLastChange
                  change={(data as any).salePriceLastChange}
                  currency={data.saleCurrency as any}
                />
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Days On Market">
                {data.saleDaysOnMarket ?? '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Untracked Period">
                {data.saleHasUntrackedPeriod === undefined ? (
                  '—'
                ) : (
                  <Chip
                    size="small"
                    label={data.saleHasUntrackedPeriod ? 'Yes' : 'No'}
                    color={data.saleHasUntrackedPeriod ? 'warning' : 'default'}
                  />
                )}
              </InfoItem>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Yields & Energy</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Energy Rating">{data.energyRating}</InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Gross Yield">
                {data.grossYield !== undefined && data.grossYield !== null
                  ? `${data.grossYield}%`
                  : '—'}
              </InfoItem>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <SectionTitle>Privacy & Flags</SectionTitle>
          <Stack direction="row" gap={1} flexWrap="wrap">
            <Chip
              size="small"
              label="Private"
              color={data.isPrivate ? 'secondary' : 'default'}
              variant={data.isPrivate ? 'filled' : 'outlined'}
            />
            <Chip
              size="small"
              label="Auction"
              color={data.isAuction ? 'warning' : 'default'}
              variant={data.isAuction ? 'filled' : 'outlined'}
            />
            <Chip
              size="small"
              label="Bank"
              color={data.isBank ? 'info' : 'default'}
              variant={data.isBank ? 'filled' : 'outlined'}
            />
            <Chip
              size="small"
              label="Connected"
              color={data.casafariConnect ? 'success' : 'default'}
              variant={data.casafariConnect ? 'filled' : 'outlined'}
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid item xs={12} md={6}>
          <SectionTitle>Sync</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Casafari Changed">
                {data.casafariChangedAt
                  ? fDateTime(data.casafariChangedAt as any)
                  : '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Casafari Synced">
                {data.casafariSyncedAt
                  ? fDateTime(data.casafariSyncedAt as any)
                  : '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Created">
                {data.createdAt ? fDateTime(data.createdAt as any) : '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Updated">
                {data.updatedAt ? fDateTime(data.updatedAt as any) : '—'}
              </InfoItem>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
}

function CasafariRentDetails({
  data,
}: {
  data: Partial<CasafariPropertyModel>;
}) {
  const { rentStatus } = data;
  return (
    <Card>
      <CardHeader title="Casafari Rent" />
      <CardContent>
        <Stack gap={3}>
          <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'background.neutral' }}>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="h6">
                {formatMoney(data.rentPriceBase, data.rentCurrency)}
              </Typography>
              <Chip
                size="small"
                label={rentStatus || '—'}
                color={statusColor(rentStatus) as any}
              />
              <Chip
                size="small"
                label="Exclusive"
                color={data.rentExclusive ? 'primary' : 'default'}
                variant={data.rentExclusive ? 'filled' : 'outlined'}
              />
              <Chip
                size="small"
                label="Below market"
                color={data.rentIsBelowMarket ? 'warning' : 'default'}
                variant={data.rentIsBelowMarket ? 'filled' : 'outlined'}
              />
              <Chip
                size="small"
                label="Untracked period"
                color={data.rentHasUntrackedPeriod ? 'warning' : 'default'}
                variant={data.rentHasUntrackedPeriod ? 'filled' : 'outlined'}
              />
            </Stack>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Rent: {formatPerSqm(data.rentPricePerSqm, data.rentCurrency)}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Currency">{data.rentCurrency}</InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Price Base">
                {formatMoney(data.rentPriceBase, data.rentCurrency)}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="€/sqm">
                {formatPerSqm(data.rentPricePerSqm, data.rentCurrency)}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Date Start">
                {data.rentDateStart
                  ? fDateTime(data.rentDateStart as any)
                  : '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Date End">
                {data.rentDateEnd ? fDateTime(data.rentDateEnd as any) : '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Days On Market">
                {data.rentDaysOnMarket ?? '—'}
              </InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Rent Period">{data.rentPeriod}</InfoItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <InfoItem label="Rental Yield">
                {data.rentalYield !== undefined && data.rentalYield !== null
                  ? `${data.rentalYield}%`
                  : '—'}
              </InfoItem>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ListingsTable({
  title,
  items,
}: {
  title: string;
  items: DeepPartial<CasafariListingModel>[];
}) {
  if (!items?.length) return null;

  return (
    <Card>
      <CardHeader title={title} />
      <CardContent>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1400 }}>
            <TableHead sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Listing ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Type Group</TableCell>
                <TableCell>Business Ops</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>ZIP</TableCell>
                <TableCell align="right">Bedrooms</TableCell>
                <TableCell align="right">Bathrooms</TableCell>
                <TableCell align="right">Rooms</TableCell>
                <TableCell align="right">Floor</TableCell>
                <TableCell align="right">Total Area</TableCell>
                <TableCell align="right">Living Area</TableCell>
                <TableCell align="right">Terrace Area</TableCell>
                <TableCell>Sale Curr</TableCell>
                <TableCell align="right">Sale Price</TableCell>
                <TableCell align="right">Sale €/sqm</TableCell>
                <TableCell>Sale Status</TableCell>
                <TableCell>Rent Curr</TableCell>
                <TableCell align="right">Rent Price</TableCell>
                <TableCell align="right">Rent €/sqm</TableCell>
                <TableCell>Rent Status</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Contact Name</TableCell>
                <TableCell>Contact Email</TableCell>
                <TableCell>Contact Phone</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Energy</TableCell>
                <TableCell>Heating</TableCell>
                <TableCell>Private</TableCell>
                <TableCell>Auction</TableCell>
                <TableCell>Bank</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>URL</TableCell>
                <TableCell>Provider Created</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((l) => (
                <TableRow key={`${l.id}-${l.listingId}`} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {l.listingId}
                  </TableCell>
                  <TableCell>{l.type}</TableCell>
                  <TableCell>{l.typeGroup}</TableCell>
                  <TableCell>
                    <Chips
                      items={
                        l.businessOperations as (string | null | undefined)[]
                      }
                    />
                  </TableCell>
                  <TableCell>{l.locationName}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {l.zipCode}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {l.bedrooms ?? '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {l.bathrooms ?? '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {l.rooms ?? '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {l.floorNumber ?? '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {l.totalArea ?? '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {l.livingArea ?? '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {l.terraceArea ?? '—'}
                  </TableCell>
                  <TableCell>{l.saleCurrency ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatMoney(l.salePrice as any, l.saleCurrency as any)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatPerSqm(
                      l.salePricePerSqm as any,
                      l.saleCurrency as any
                    )}
                  </TableCell>
                  <TableCell>
                    {l.saleStatus ? (
                      <Chip
                        size="small"
                        label={l.saleStatus}
                        color={statusColor(l.saleStatus) as any}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{l.rentCurrency ?? '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatMoney(l.rentPrice as any, l.rentCurrency as any)}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatPerSqm(
                      l.rentPricePerSqm as any,
                      l.rentCurrency as any
                    )}
                  </TableCell>
                  <TableCell>
                    {l.rentStatus ? (
                      <Chip
                        size="small"
                        label={l.rentStatus}
                        color={statusColor(l.rentStatus) as any}
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{l.agency ?? '—'}</TableCell>
                  <TableCell>{l.contactsInfo?.name ?? '—'}</TableCell>
                  <TableCell>
                    {l.contactsInfo?.email ? (
                      <Link href={`mailto:${l.contactsInfo.email}`}>
                        {l.contactsInfo.email}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {l.contactsInfo?.phone ? (
                      <Link href={`tel:${l.contactsInfo.phone}`}>
                        {l.contactsInfo.phone}
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{l.sourceName ?? '—'}</TableCell>
                  <TableCell>{l.energyRating ?? '—'}</TableCell>
                  <TableCell>{l.heatingType ?? '—'}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Private"
                      color={l.isPrivate ? 'secondary' : 'default'}
                      variant={l.isPrivate ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Auction"
                      color={l.isAuction ? 'warning' : 'default'}
                      variant={l.isAuction ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label="Bank"
                      color={l.isBank ? 'info' : 'default'}
                      variant={l.isBank ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {l.isActive === undefined ? (
                      '—'
                    ) : (
                      <Chip
                        size="small"
                        label={l.isActive ? 'Active' : 'Inactive'}
                        color={l.isActive ? 'success' : 'default'}
                        variant={l.isActive ? 'filled' : 'outlined'}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {l.url ? (
                      <Link href={l.url} target="_blank" rel="noopener">
                        Open
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {l.providerCreatedAt
                      ? fDateTime(l.providerCreatedAt as any)
                      : '—'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {fDateTime(l.createdAt as any)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {fDateTime(l.updatedAt as any)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function ContactsTable({
  items,
}: {
  items: DeepPartial<CasafariListingModel>[];
}) {
  if (!items?.length) return null;

  const renderPrice = (l: DeepPartial<CasafariListingModel>) => {
    const hasSale = l.salePrice !== undefined && l.salePrice !== null;
    const hasRent = l.rentPrice !== undefined && l.rentPrice !== null;
    if (hasSale)
      return (
        <Stack direction="row" gap={1} alignItems="center">
          <Chip size="small" label="Sale" color="primary" variant="outlined" />
          <Typography variant="body2">
            {formatMoney(l.salePrice as any, l.saleCurrency as any)}
          </Typography>
        </Stack>
      );
    if (hasRent)
      return (
        <Stack direction="row" gap={1} alignItems="center">
          <Chip
            size="small"
            label="Rent"
            color="secondary"
            variant="outlined"
          />
          <Typography variant="body2">
            {formatMoney(l.rentPrice as any, l.rentCurrency as any)}
          </Typography>
        </Stack>
      );
    return '—';
  };

  return (
    <Card>
      <CardHeader title="Contacts" />
      <CardContent>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead sx={{ '& th': { whiteSpace: 'nowrap' } }}>
              <TableRow>
                <TableCell>Agency</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Website</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Provider Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((l) => (
                <TableRow key={`${l.id}-${l.listingId}`} hover>
                  <TableCell>{l.agency ?? '—'}</TableCell>
                  <TableCell>
                    {l.contactsInfo?.name ||
                    l.contactsInfo?.phone ||
                    l.contactsInfo?.email ? (
                      <Stack spacing={0.5}>
                        {l.contactsInfo?.name ? (
                          <Typography variant="body2">
                            {l.contactsInfo.name}
                          </Typography>
                        ) : null}
                        {l.contactsInfo?.phone || l.contactsInfo?.email ? (
                          <Stack direction="row" gap={1} flexWrap="wrap">
                            {l.contactsInfo?.phone ? (
                              <Link href={`tel:${l.contactsInfo.phone}`}>
                                {l.contactsInfo.phone}
                              </Link>
                            ) : null}
                            {l.contactsInfo?.email ? (
                              <Link href={`mailto:${l.contactsInfo.email}`}>
                                {l.contactsInfo.email}
                              </Link>
                            ) : null}
                          </Stack>
                        ) : null}
                      </Stack>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {l.url ? (
                      <Link href={l.url} target="_blank" rel="noopener">
                        Open
                      </Link>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{l.sourceName ?? '—'}</TableCell>
                  <TableCell>
                    {l.isActive === undefined ? (
                      '—'
                    ) : (
                      <Chip
                        size="small"
                        label={l.isActive ? 'Active' : 'Inactive'}
                        color={l.isActive ? 'success' : 'default'}
                        variant={l.isActive ? 'filled' : 'outlined'}
                      />
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {renderPrice(l)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {l.providerCreatedAt
                      ? fDateTime(l.providerCreatedAt as any)
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

export default function PropertyCasafariView({ property }: Props) {
  const casafari = property.casafari as
    | DeepPartial<CasafariPropertyModel>
    | undefined;

  return (
    <Stack gap={4}>
      <Card>
        <CardHeader
          title="Casafari Property"
          subheader={casafari?.casafariPropertyId}
        />
        <CardContent>
          {casafari ? (
            <CasafariDetails
              data={casafari as Partial<CasafariPropertyModel>}
            />
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No Casafari data available for this property.
            </Typography>
          )}
        </CardContent>
      </Card>

      {casafari && (
        <CasafariRentDetails
          data={casafari as Partial<CasafariPropertyModel>}
        />
      )}

      {casafari?.listings && casafari.listings.length > 0 && (
        <ContactsTable
          items={casafari.listings as DeepPartial<CasafariListingModel>[]}
        />
      )}

      {casafari?.listings && casafari.listings.length > 0 && (
        <ListingsTable
          title="Casafari Listings"
          items={casafari.listings as DeepPartial<CasafariListingModel>[]}
        />
      )}
    </Stack>
  );
}
